// @flow
import { WarningOutlined } from '@ant-design/icons';
import React, { Component } from 'react';
import qs from 'query-string';
import styled from 'styled-components';
import { Link } from '@reach/router';

import notification from 'antd/lib/notification';

import { tireCalculationApi, vehiclePlanApi } from '../../../../lib/api';
import type {
  TireCalculation,
  ListState,
  VehiclePlan
} from '../../../../lib/types';
import {
  getListInitialState,
  plus,
  toLocalStringRu
} from '../../../../lib/helpers';
import { Section } from '../../../../components/layout';
import { AntTable } from '../../../../components/ui';
import Popover from '../../../../components/ui/Popover';
import { TableHeader } from '../../../../components/ui/Table';

import { COLUMNS, MONTH } from '../lib';

import Filter from './components/Filter';
import { headerPanel } from '../components/Common';
import type { ParamsFilter } from './components/Filter';
import { notificationLoading } from '../../../../components/Notifications';
import { calculationStatusEnum } from '../../../../lib/enum';
import Button from 'antd/lib/button';

type Props = {
  location: Location & { state: { page: number } },
  vehiclePlanId: number
};

type State = ListState<TireCalculation> & {
  columns: any,
  tireCalculation: ?TireCalculation,
  filter: ParamsFilter,
  vehiclePlan: ?VehiclePlan
};

const StyledIcon = styled(WarningOutlined)`
  position: relative;
  top: -1px;
  right: -4px;
`;

export default class extends Component<Props, State> {
  state = {
    ...getListInitialState(),
    columns: [
      ...COLUMNS.map(column =>
        column.key === 'licensePlate'
          ? {
              ...column,
              render: (licensePlate: string, record: TireCalculation) => {
                return record.hasNoCost
                  ? licensePlate && (
                      <Popover
                        placement="right"
                        width={150}
                        zIndex={3000}
                        content={
                          'Необходимо проверить наличие аналога шины или стоимости в справочнике МТРиО'
                        }
                      >
                        <Link
                          to={`/vehicles/${record.selfVehiclePlanVehicle.vehicleId}`}
                        >
                          {licensePlate}{' '}
                        </Link>
                        <StyledIcon
                          style={{ color: '#eccd0e' }}
                        />
                      </Popover>
                    )
                  : licensePlate && (
                      <Link
                        to={`/vehicles/${record.selfVehiclePlanVehicle.vehicleId}`}
                      >
                        {licensePlate}
                      </Link>
                    );
              }
            }
          : { ...column }
      ),
      ...MONTH.map((month, index) => ({
        title: month.title,
        children: [
          {
            title: (
              <TableHeader width="120px">
                Кол-во автошин подлежащих замене
              </TableHeader>
            ),
            dataIndex: `months[${index}].count`
          },
          {
            title: (
              <TableHeader width="120px">
                Стоимость замены всех автошин
              </TableHeader>
            ),
            dataIndex: `months[${index}].cost`,
            render: (cost: any) => {
              return toLocalStringRu(cost) ?? 0;
            }
          }
        ]
      })),
      {
        title: 'Итог',
        dataIndex: 'sum'
      }
    ],
    filter: {},
    vehiclePlan: null,
    springPassCalculation: null
  };

  componentDidMount() {
    const { page, ...filter } = qs.parse(window.location.search);
    this.setState({ filter }, () => this.getData());
  }

  getData = async () => {
    this.setState({ loading: true });
    try {
      await this.getVehiclePlan();
      await this.fetch();
    } finally {
      this.setState({ loading: false });
    }
  };

  getVehiclePlan = async () => {
    const { vehiclePlanId } = this.props;
    try {
      const vehiclePlan = await vehiclePlanApi.get(vehiclePlanId);
      this.setState({ vehiclePlan });
    } catch (error) {
      notification.warning({ message: 'Не удалось запросить данные' });
    }
  };

  fetch = async () => {
    const { vehiclePlanId } = this.props;

    const itogCalculation = (data: any) => {
      const itog = {
        months: Array(12)
          .fill()
          .map(() => ({ cost: 0 })),
        sum: 0
      };
      data.forEach((item: any) => {
        itog.sum = plus(item.sum || 0, itog.sum);
        item.months.forEach((month: any, index) => {
          itog.months[index].cost = plus(
            itog.months[index].cost,
            month.cost || 0
          );
        });
      });
      return itog;
    };

    try {
      let { data } = await tireCalculationApi.fetch({
        ...this.state.filter,
        vehiclePlanId,
        page: undefined,
        pageSize: undefined
      });
      if (!data) {
        notification.warning({ message: 'Не удалось запросить данные' });
        return;
      }
      if (data.length) {
        data = [...data, itogCalculation(data)];
      }

      this.setState({ data });
    } catch (e) {
      notification.error({ message: 'Не удалось запросить данные' });
    }
  };

  applyFilter = (filter: any) => {
    this.setState({ filter }, this.getData);
  };

  cleanFilter = async () => {
    this.setState({ filter: {} }, this.getData);
  };

  changeStatus = async () => {
    const { vehiclePlanId } = this.props;
    try {
      notificationLoading({
        message: 'Сохранение данных',
        key: 'saving'
      });
      await tireCalculationApi.changeStatus(
        vehiclePlanId,
        calculationStatusEnum.calculationDone
      );
      this.getData();
    } catch (error) {
      notification.error({ message: 'Не удалось обновить данные данные' });
    } finally {
      notification.close('saving');
    }
  };

  calculate = async () => {
    const { vehiclePlanId } = this.props;
    try {
      notificationLoading({
        message: 'Сохранение данных',
        key: 'saving'
      });
      await tireCalculationApi.calculate(vehiclePlanId);
      this.getData();
    } catch (error) {
      notification.error({ message: 'Не удалось обновить данные данные' });
    } finally {
      notification.close('saving');
    }
  };

  isDraft = () =>
    this.state.vehiclePlan?.tireCalculationStatus ===
    calculationStatusEnum.draft;

  render() {
    const { vehiclePlanId } = this.props;
    const { columns, loading, data } = this.state;
    return (
      <>
        {headerPanel({
          vehiclePlanId,
          title: 'Автошины',
          passTab: true
        })}
        <Section>
          <Filter
            applyFilter={this.applyFilter}
            cleanFilter={this.cleanFilter}
            filter={this.state.filter}
          />
          <AntTable
            loading={loading}
            data={[...data]}
            columns={columns}
            bordered
            scroll={{
              x: 5000,
              y: 'calc(100vh - 400px)'
            }}
          />
        </Section>
        {this.isDraft() && (
          <Section style={{ padding: '16px' }}>
            <Button type="primary" onClick={this.calculate}>
              Рассчитать
            </Button>
            <Button
              style={{ marginLeft: '16px' }}
              type="primary"
              onClick={this.changeStatus}
            >
              Утвердить
            </Button>
          </Section>
        )}
      </>
    );
  }
}
